import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { Ban, CheckCircle2, Loader2, Pencil, Plus, RefreshCw, Search } from 'lucide-react';
import {
    useCatchFormCatalog,
    useSetCatchFormCatalogActive,
    useUpsertCatchFormCatalog,
    type CatchCatalogCategory,
    type CatchCatalogRow,
} from './hooks/useCatchFormCatalogAdmin';

const TABS: { id: CatchCatalogCategory; label: string; help: string }[] = [
    {
        id: 'modality',
        label: 'Modalidades',
        help: 'Técnicas del formulario de captura (spinning, eging…).',
    },
    {
        id: 'location',
        label: 'Desde',
        help: 'Ubicaciones «desde dónde» (orilla, kayak, barco…).',
    },
    {
        id: 'lure',
        label: 'Señuelos',
        help: 'Tipos de señuelo / montaje (Texas, Free Rig, jig…).',
    },
    {
        id: 'bait',
        label: 'Cebos',
        help: 'Tipos de cebo natural o artificial.',
    },
];

const WATER_OPTIONS: { value: 'dulce' | 'salada' | 'both'; label: string }[] = [
    { value: 'both', label: 'Ambas' },
    { value: 'dulce', label: 'Dulce' },
    { value: 'salada', label: 'Salada' },
];

type FormState = {
    id: string | null;
    name: string;
    slug: string;
    water_type: 'dulce' | 'salada' | 'both';
    icon: string;
    visual_kind: string;
    material_kind: '' | 'cebo' | 'señuelo';
    display_order: string;
    is_active: boolean;
    is_default: boolean;
};

const EMPTY_FORM: FormState = {
    id: null,
    name: '',
    slug: '',
    water_type: 'both',
    icon: '',
    visual_kind: '',
    material_kind: '',
    display_order: '',
    is_active: true,
    is_default: false,
};

function waterLabel(v: string) {
    return WATER_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

export function CatchFormCatalogPage() {
    const [tab, setTab] = useState<CatchCatalogCategory>('lure');
    const [search, setSearch] = useState('');
    const [includeInactive, setIncludeInactive] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const { data, isLoading, refetch, isFetching } = useCatchFormCatalog(tab, includeInactive);
    const upsert = useUpsertCatchFormCatalog();
    const setActive = useSetCatchFormCatalogActive();

    const rows = useMemo(() => {
        const list = data ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (r) =>
                r.name.toLowerCase().includes(q) ||
                r.slug.toLowerCase().includes(q) ||
                (r.icon ?? '').includes(q),
        );
    }, [data, search]);

    const tabMeta = TABS.find((t) => t.id === tab)!;

    const openCreate = () => {
        setForm({ ...EMPTY_FORM });
        setErrorMsg(null);
        setDialogOpen(true);
    };

    const openEdit = (row: CatchCatalogRow) => {
        setForm({
            id: row.id,
            name: row.name,
            slug: row.slug,
            water_type: row.water_type,
            icon: row.icon ?? '',
            visual_kind: row.visual_kind ?? '',
            material_kind: row.material_kind ?? '',
            display_order: String(row.display_order ?? ''),
            is_active: row.is_active,
            is_default: row.is_default,
        });
        setErrorMsg(null);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setErrorMsg(null);
        if (!form.name.trim()) {
            setErrorMsg('El nombre es obligatorio');
            return;
        }
        try {
            await upsert.mutateAsync({
                id: form.id,
                category: tab,
                name: form.name,
                slug: form.slug,
                water_type: form.water_type,
                icon: form.icon,
                visual_kind: tab === 'lure' ? form.visual_kind : undefined,
                material_kind: tab === 'modality' ? form.material_kind : undefined,
                display_order: form.display_order.trim()
                    ? Number(form.display_order)
                    : null,
                is_active: form.is_active,
                is_default: tab === 'modality' ? form.is_default : false,
            });
            setDialogOpen(false);
        } catch (e) {
            setErrorMsg(e instanceof Error ? e.message : 'Error al guardar');
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Catálogo de captura"
                description="Modalidades, «Desde», señuelos y cebos del formulario de añadir captura"
            >
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={openCreate}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                        <Plus size={14} />
                        Añadir
                    </button>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted"
                    >
                        <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                        Actualizar
                    </button>
                </div>
            </PageHeader>

            <div className="flex flex-wrap gap-2">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                            setTab(t.id);
                            setSearch('');
                        }}
                        className={cn(
                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                            tab === t.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted/60 text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <p className="text-sm text-muted-foreground">{tabMeta.help}</p>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre o slug…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
                    />
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                    />
                    Incluir inactivos
                </label>
                <span className="text-xs text-muted-foreground">{rows.length} ítems</span>
            </div>

            <div className="rounded-xl border border-border overflow-hidden bg-card">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
                        <Loader2 className="animate-spin" size={18} />
                        Cargando…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center text-sm text-muted-foreground">
                        No hay ítems en esta categoría
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground text-left">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Nombre</th>
                                    <th className="px-3 py-2 font-medium">Slug</th>
                                    <th className="px-3 py-2 font-medium">Agua</th>
                                    {tab === 'modality' && (
                                        <th className="px-3 py-2 font-medium">Material</th>
                                    )}
                                    <th className="px-3 py-2 font-medium">Icono</th>
                                    <th className="px-3 py-2 font-medium">Orden</th>
                                    <th className="px-3 py-2 font-medium">Estado</th>
                                    <th className="px-3 py-2 font-medium w-28">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-t border-border/60 hover:bg-muted/20"
                                    >
                                        <td className="px-3 py-2 font-medium">{row.name}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                                            {row.slug}
                                        </td>
                                        <td className="px-3 py-2">{waterLabel(row.water_type)}</td>
                                        {tab === 'modality' && (
                                            <td className="px-3 py-2 capitalize">
                                                {row.material_kind ?? '—'}
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-lg">{row.icon || '—'}</td>
                                        <td className="px-3 py-2">{row.display_order}</td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={cn(
                                                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
                                                    row.is_active
                                                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                                                        : 'bg-muted text-muted-foreground',
                                                )}
                                            >
                                                {row.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    title="Editar"
                                                    onClick={() => openEdit(row)}
                                                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    title={row.is_active ? 'Desactivar' : 'Activar'}
                                                    disabled={setActive.isPending}
                                                    onClick={() =>
                                                        setActive.mutate({
                                                            id: row.id,
                                                            isActive: !row.is_active,
                                                        })
                                                    }
                                                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                                                >
                                                    {row.is_active ? (
                                                        <Ban size={14} />
                                                    ) : (
                                                        <CheckCircle2 size={14} />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {dialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl p-5 space-y-4">
                        <h3 className="text-lg font-semibold">
                            {form.id ? 'Editar' : 'Nuevo'} · {tabMeta.label}
                        </h3>

                        <div className="grid gap-3">
                            <label className="space-y-1 text-sm">
                                <span className="text-muted-foreground">Nombre *</span>
                                <input
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, name: e.target.value }))
                                    }
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                    placeholder={
                                        tab === 'lure'
                                            ? 'Montaje Free Rig'
                                            : tab === 'bait'
                                              ? 'Camarón'
                                              : tab === 'location'
                                                ? 'Orilla'
                                                : 'Spinning'
                                    }
                                />
                            </label>

                            <label className="space-y-1 text-sm">
                                <span className="text-muted-foreground">
                                    Slug {form.id ? '' : '(opcional, se genera del nombre)'}
                                </span>
                                <input
                                    value={form.slug}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, slug: e.target.value }))
                                    }
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background font-mono text-xs"
                                    placeholder="montaje-free-rig"
                                />
                            </label>

                            <label className="space-y-1 text-sm">
                                <span className="text-muted-foreground">Tipo de agua *</span>
                                <select
                                    value={form.water_type}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            water_type: e.target.value as FormState['water_type'],
                                        }))
                                    }
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                >
                                    {WATER_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {tab === 'modality' && (
                                <label className="space-y-1 text-sm">
                                    <span className="text-muted-foreground">
                                        Material típico (cebo / señuelo)
                                    </span>
                                    <select
                                        value={form.material_kind}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                material_kind: e.target
                                                    .value as FormState['material_kind'],
                                            }))
                                        }
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                    >
                                        <option value="">Sin fijar</option>
                                        <option value="cebo">Cebo</option>
                                        <option value="señuelo">Señuelo</option>
                                    </select>
                                </label>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <label className="space-y-1 text-sm">
                                    <span className="text-muted-foreground">Icono (emoji)</span>
                                    <input
                                        value={form.icon}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, icon: e.target.value }))
                                        }
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                        placeholder="🪝"
                                    />
                                </label>
                                <label className="space-y-1 text-sm">
                                    <span className="text-muted-foreground">Orden</span>
                                    <input
                                        type="number"
                                        value={form.display_order}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                display_order: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                        placeholder="auto"
                                    />
                                </label>
                            </div>

                            {tab === 'lure' && (
                                <label className="space-y-1 text-sm">
                                    <span className="text-muted-foreground">
                                        Visual kind (asset, opcional)
                                    </span>
                                    <input
                                        value={form.visual_kind}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                visual_kind: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background font-mono text-xs"
                                        placeholder="free-rig"
                                    />
                                </label>
                            )}

                            <div className="flex flex-wrap gap-4 text-sm">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={form.is_active}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                is_active: e.target.checked,
                                            }))
                                        }
                                    />
                                    Activo
                                </label>
                                {tab === 'modality' && (
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={form.is_default}
                                            onChange={(e) =>
                                                setForm((f) => ({
                                                    ...f,
                                                    is_default: e.target.checked,
                                                }))
                                            }
                                        />
                                        Por defecto
                                    </label>
                                )}
                            </div>
                        </div>

                        {errorMsg && (
                            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                                {errorMsg}
                            </p>
                        )}

                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setDialogOpen(false)}
                                className="px-3 py-2 rounded-lg text-sm bg-muted hover:bg-muted/80"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                disabled={upsert.isPending}
                                onClick={() => void handleSave()}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                            >
                                {upsert.isPending ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
